import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Mail, Key, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PartenairePaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  const sessionId = searchParams.get('session_id');
  const memberId = searchParams.get('member_id');

  useEffect(() => {
    if (sessionId && memberId) {
      verifyPayment();
    } else {
      setStatus('error');
      setMessage('Paramètres de paiement manquants');
    }
  }, [sessionId, memberId]);

  const verifyPayment = async () => {
    try {
      const response = await fetch(
        `${API}/api/partenaire/subscription/verify/${sessionId}?member_id=${memberId}`
      );
      const data = await response.json();

      if (data.status === 'success') {
        setStatus('success');
        setMessage(data.message);
      } else if (data.status === 'pending') {
        // Retry after a delay
        setTimeout(verifyPayment, 2000);
      } else {
        setStatus('error');
        setMessage(data.message || 'Une erreur est survenue');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Erreur lors de la vérification du paiement');
    }
  };

  return (
    <div className="min-h-screen bg-purple-900 flex items-center justify-center p-6">
      <Card className="max-w-lg w-full border-0 shadow-2xl bg-white/10 backdrop-blur">
        <CardContent className="p-8">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-purple-300 animate-spin" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-4">
                Vérification du paiement...
              </h2>
              <p className="text-white/70">
                Veuillez patienter, nous validons votre abonnement.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-4">
                🎉 Félicitations !
              </h2>
              <p className="text-white/70 mb-6">
                Votre abonnement Page Blanche est maintenant actif.
              </p>

              <div className="bg-white/10 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-5 h-5 text-purple-300" />
                  <span className="text-white/80">Email envoyé avec vos identifiants</span>
                </div>
                <div className="flex items-center gap-3">
                  <Key className="w-5 h-5 text-purple-300" />
                  <span className="text-white/80">Mot de passe temporaire généré</span>
                </div>
              </div>

              <p className="text-white/60 text-sm mb-6">
                Consultez votre boîte email pour récupérer vos codes d'accès. 
                <br /><span className="text-amber-400">⚠️ Pensez à vérifier vos spams si vous ne recevez rien !</span>
              </p>

              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/partenaire/login')}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Accéder à mon dashboard
                </Button>
                <Button 
                  onClick={() => navigate('/')}
                  variant="ghost"
                  className="w-full text-white/70 hover:text-white hover:bg-white/10"
                >
                  Retour à l'accueil
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-4">
                Erreur de paiement
              </h2>
              <p className="text-white/70 mb-6">
                {message}
              </p>
              <Button 
                onClick={() => navigate('/reseau/partenaire/inscription')}
                className="bg-purple-500 hover:bg-purple-600 text-white"
              >
                Réessayer l'inscription
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
